import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const SmooveMove: React.FC = () => {
  const [activeView, setActiveView] = useState<'desktop' | 'mobile'>('desktop')
  const [activeComparison, setActiveComparison] = useState<'before' | 'after'>('before')

  useEffect(() => {
    // Detect screen size and set appropriate default view
    const checkScreenSize = () => {
      setActiveView(window.innerWidth >= 768 ? 'desktop' : 'mobile')
    }

    // Set on initial load
    checkScreenSize()

    // Listen for window resize
    window.addEventListener('resize', checkScreenSize)
    
    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const projectDetails = {
    client: 'Smoove Move Logistics',
    industry: 'Moving & Logistics',
    services: ['Landing Page Design', 'Logo Rebrand', 'Brand Identity', 'Visual Design'],
    year: '2024',
    url: 'https://smoovemovelogistics.com',
    displayUrl: 'smoovemovelogistics.com',
  }

  const challenges = [
    'Website looked like a generic AI template builder—zero credibility or trust',
    'Tacky fake helper bot that damaged credibility instead of helping conversions',
    'Incredibly confusing navigation and cluttered layout driving visitors away',
    'Outdated brand identity failing to reflect their premium service quality',
    'Generic logo with no personality, memorability, or professional polish',
    'No cohesive visual system creating a disjointed, amateur appearance',
  ]

  const solutions = [
    'Complete logo redesign with modern, professional aesthetic',
    'Comprehensive brand identity system (colors, typography, visual language)',
    'Removed fake helper bot, replaced with strategic CTAs and trust signals',
    'Landing page with full-screen hero video for immediate impact',
    'Mobile-first responsive design optimized for conversions',
    'Fast, SEO-optimized Next.js implementation',
  ]

  const results = [
    {
      metric: 'Load Time',
      before: '4.2s',
      after: '0.8s',
      improvement: '81% faster',
    },
    {
      metric: 'Mobile Score',
      before: '62/100',
      after: '98/100',
      improvement: '58% improvement',
    },
    {
      metric: 'User Experience',
      before: 'Cluttered',
      after: 'Premium',
      improvement: 'Complete transformation',
    },
  ]

  const techStack = ['Next.js 16', 'React 19', 'TypeScript', 'Tailwind CSS 4', 'Vercel']

  const brandColors = [
    { name: 'Liftoff', hex: '#ffa300', description: 'Energy & Forward Motion' },
    { name: 'Stratosphere', hex: '#508fff', description: 'Clarity & Open Skies' },
    { name: 'Orbit', hex: '#0032a3', description: 'Trust & Smooth Journey' },
    { name: 'Cosmos', hex: '#2b1e59', description: 'Innovation & Character' },
    { name: 'Mission Control', hex: '#222222', description: 'Precision & Professionalism' },
  ]

  const brandElements = [
    {
      title: 'Logo Design',
      description: 'Bold, modern wordmark with integrated "Ü" icon. The design balances professionalism with approachability, perfect for a premium moving service.',
      image: '/case-studies/smoove-move/logo.png',
      bg: 'bg-gradient-to-br from-neutral-950 to-neutral-900',
      type: 'image',
    },
    {
      title: 'Brand Icon',
      description: 'Distinctive "Ü" mark that serves as a memorable visual identifier across all touchpoints. The orange icon creates instant brand recognition.',
      image: '/case-studies/smoove-move/icon.png',
      bg: 'bg-gradient-to-br from-neutral-950 to-neutral-900',
      type: 'image',
    },
    {
      title: 'Color Palette',
      description: 'Strategic color system combining energetic orange, trustworthy blue, and professional dark tones for a cohesive brand identity.',
      type: 'colors',
    },
    {
      title: 'Astronaut Mascot',
      description: 'Custom character that adds personality and approachability to the brand. The astronaut theme connects to their "moving you forward" mission.',
      image: '/case-studies/smoove-move/ASTRO.png',
      bg: 'bg-gradient-to-br from-neutral-950 to-neutral-900',
      type: 'image',
    },
  ]

  const digitalFeatures = [
    {
      title: 'Custom Built from Scratch',
      description: 'Entirely custom-coded landing page with clean, professional animations and design. No templates, no shortcuts—pure craftsmanship.',
    },
    {
      title: 'Premium Animations',
      description: 'Smooth, professional micro-interactions and transitions throughout. Hero video background, hover effects, and scroll animations create an engaging experience.',
    },
    {
      title: 'Google Reviews Integration',
      description: 'Strategic placement of customer testimonials and Google reviews to build trust and credibility with potential clients.',
    },
    {
      title: 'Service Showcase',
      description: 'Clean grid layout highlighting six core moving services with hover effects and clear CTAs designed for maximum conversions.',
    },
    {
      title: 'Mobile-First Design',
      description: 'Responsive layouts optimized for touch interactions, ensuring perfect presentation across all devices and screen sizes.',
    },
    {
      title: 'Performance Optimized',
      description: 'Next.js implementation with server-side rendering, image optimization, and lazy loading for lightning-fast load times.',
    },
  ]

  return (
    <div className="min-h-screen bg-black">
      {/* Hero with Interactive Preview */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Logo */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <img
                src="/case-studies/smoove-move/logo.png"
                alt="Smoove Move Logistics"
                className="h-32 md:h-40 lg:h-48 w-auto mx-auto"
              />
            </motion.div>

            <p className="text-lg md:text-xl text-neutral-400 font-light leading-relaxed max-w-3xl mx-auto mb-8">
              Landing page rebuild, logo rebrand, and complete brand identity for a full-service moving company.
            </p>

            {/* Before/After Switch */}
            <div className="flex flex-col sm:flex-row gap-6 items-center justify-center mb-8">
              <div className="relative inline-flex items-center p-2 bg-gradient-to-br from-neutral-900 to-neutral-950 rounded-full shadow-xl border border-neutral-800/50">
                {/* Sliding Background */}
                <div
                  className={`absolute top-2 bottom-2 w-[calc(50%-8px)] rounded-full transition-all duration-500 ease-out ${
                    activeComparison === 'before'
                      ? 'left-2 bg-gradient-to-br from-red-500 to-red-600 shadow-2xl shadow-red-500/30'
                      : 'left-[calc(50%+4px)] bg-gradient-to-br from-green-500 to-green-600 shadow-2xl shadow-green-500/30'
                  }`}
                />

                {/* Before Button */}
                <button
                  onClick={() => setActiveComparison('before')}
                  className={`group relative px-10 py-4 rounded-full text-base font-medium transition-all duration-500 ease-out z-10 ${
                    activeComparison === 'before'
                      ? 'text-white'
                      : 'text-neutral-500 hover:text-white'
                  }`}
                >
                  <span className="relative flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      activeComparison === 'before'
                        ? 'bg-white shadow-lg shadow-white/50'
                        : 'bg-neutral-400 group-hover:bg-red-400 group-hover:scale-110'
                    }`} />
                    Before
                  </span>
                </button>

                {/* After Button */}
                <button
                  onClick={() => setActiveComparison('after')}
                  className={`group relative px-10 py-4 rounded-full text-base font-medium transition-all duration-500 ease-out z-10 ${
                    activeComparison === 'after'
                      ? 'text-white'
                      : 'text-neutral-500 hover:text-white'
                  }`}
                >
                  <span className="relative flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      activeComparison === 'after'
                        ? 'bg-white shadow-lg shadow-white/50'
                        : 'bg-neutral-400 group-hover:bg-green-400 group-hover:scale-110'
                    }`} />
                    After
                  </span>
                </button>
              </div>

              {/* Desktop/Mobile Toggle */}
              <div className="relative inline-flex items-center gap-1.5 p-1.5 bg-gradient-to-br from-neutral-900 to-neutral-950 rounded-full shadow-lg border border-neutral-800/50">
                <button
                  onClick={() => setActiveView('desktop')}
                  className={`group relative px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-400 ease-out ${
                    activeView === 'desktop'
                      ? 'bg-gradient-to-br from-neutral-800 to-black text-white shadow-xl shadow-neutral-800/20 scale-105'
                      : 'text-neutral-500 hover:text-white hover:bg-neutral-900/70 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <svg className={`w-4 h-4 transition-transform duration-300 ${activeView !== 'desktop' ? 'group-hover:scale-110' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="2" y="3" width="20" height="14" rx="2" strokeWidth="2" />
                      <path d="M8 21h8" strokeWidth="2" strokeLinecap="round" />
                      <path d="M12 17v4" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Desktop
                  </span>
                  {activeView !== 'desktop' && (
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-neutral-800/10 to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                </button>
                <button
                  onClick={() => setActiveView('mobile')}
                  className={`group relative px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-400 ease-out ${
                    activeView === 'mobile'
                      ? 'bg-gradient-to-br from-neutral-800 to-black text-white shadow-xl shadow-neutral-800/20 scale-105'
                      : 'text-neutral-500 hover:text-white hover:bg-neutral-900/70 hover:shadow-md hover:scale-102'
                  }`}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <svg className={`w-4 h-4 transition-transform duration-300 ${activeView !== 'mobile' ? 'group-hover:scale-110' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="5" y="2" width="14" height="20" rx="2" strokeWidth="2" />
                      <path d="M12 18h.01" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Mobile
                  </span>
                  {activeView !== 'mobile' && (
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-neutral-800/10 to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Browser Chrome */}
            <div className={`px-4 py-3 flex items-center gap-2 border-b border-neutral-800 transition-colors ${
              activeComparison === 'before' ? 'bg-red-50' : 'bg-green-50'
            }`}>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 mx-4 bg-white rounded px-3 py-1 text-xs text-neutral-500 font-light">
                https://{projectDetails.displayUrl}
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                activeComparison === 'before'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {activeComparison === 'before' ? 'BEFORE' : 'AFTER'}
              </div>
            </div>

            {/* iframe Container */}
            <div className={`bg-neutral-900 flex items-start justify-center ${
              activeView === 'desktop' ? 'p-0' : 'p-8'
            }`}>
              {activeView === 'desktop' ? (
                /* Desktop view - full width, actual responsive scaling */
                <motion.div
                  key={`${activeComparison}-desktop`}
                  initial={{ opacity: 0, scale: 0.98, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full h-[600px]"
                >
                  <iframe
                    src={activeComparison === 'before' ? 'https://smoove-old.vercel.app' : projectDetails.url}
                    className="w-full h-full bg-white border-none"
                    title={`Smoove Move Logistics - Desktop View`}
                    loading="lazy"
                  />
                </motion.div>
              ) : (
                /* Mobile view - exact phone dimensions with chrome */
                <motion.div
                  key={`${activeComparison}-mobile`}
                  initial={{ opacity: 0, scale: 0.98, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="relative bg-neutral-800 rounded-[30px] p-2 shadow-2xl"
                  style={{
                    width: '390px',  // iPhone viewport width
                    height: '894px', // iPhone viewport height + chrome
                  }}
                >
                  {/* Phone Frame/Chrome */}
                  <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-16 h-1.5 bg-neutral-700 rounded-full"></div>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-neutral-700 rounded-full"></div>
                  
                  {/* Screen Area */}
                  <div className="bg-black rounded-[25px] overflow-hidden" style={{ width: '390px', height: '844px' }}>
                    <iframe
                      src={activeComparison === 'before' ? 'https://smoove-old.vercel.app' : projectDetails.url}
                      className="w-full h-full bg-white" 
                      title={`Smoove Move Logistics - Mobile View`}
                      loading="lazy"
                    />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Info Bar */}
            <div className="bg-neutral-950 px-6 py-4 text-center border-t border-neutral-800">
              <p className="text-xs text-neutral-500 font-light">
                Interactive preview — Click and scroll to explore
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Client</div>
              <div className="text-white font-light">{projectDetails.client}</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Industry</div>
              <div className="text-white font-light">{projectDetails.industry}</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Services</div>
              <div className="text-white font-light text-sm">{projectDetails.services.join(', ')}</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="text-sm text-neutral-500 mb-2 font-light">Year</div>
              <div className="text-white font-light">{projectDetails.year}</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8">The Challenge</h2>
            <p className="text-neutral-400 font-light leading-relaxed mb-8">
              Smoove Move Logistics came to us with a website that was actively hurting their business. Their online presence looked like it came from a generic AI template builder—complete with a tacky fake helper bot, confusing navigation, and cluttered design. Despite offering premium moving services, their website made them look amateur and untrustworthy, driving potential customers away instead of converting them.
            </p>
            <ul className="space-y-3">
              {challenges.map((challenge, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3 text-neutral-400 font-light"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span className="text-white mt-1">—</span>
                  {challenge}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8">The Solution</h2>
            <p className="text-neutral-400 font-light leading-relaxed mb-8">
              We delivered a comprehensive rebrand including a bold new logo, complete brand identity system, and a high-converting landing page. Modern design, premium animations, and strategic content placement transformed their brand presence.
            </p>
            <ul className="space-y-3">
              {solutions.map((solution, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3 text-neutral-400 font-light"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span className="text-white mt-1">—</span>
                  {solution}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Brand Elements */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Brand Elements</h2>
            <p className="text-neutral-400 font-light">Building a complete visual identity system.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {brandElements.map((element, index) => (
              <motion.div
                key={element.title}
                className="bg-neutral-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                {element.type === 'image' ? (
                  <div className={`aspect-square ${element.bg} flex items-center justify-center p-12`}>
                    <img
                      src={element.image}
                      alt={element.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="aspect-square bg-gradient-to-br from-neutral-950 to-neutral-900 flex items-center justify-center p-12">
                    <div className="w-full space-y-5">
                      {brandColors.map((color) => (
                        <div key={color.hex} className="flex items-center gap-4">
                          <div
                            className="w-20 h-20 rounded-xl shadow-lg border border-neutral-800 flex-shrink-0"
                            style={{ backgroundColor: color.hex }}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-base text-white">{color.name}</div>
                            <div className="text-sm text-neutral-500 font-mono">{color.hex}</div>
                            <div className="text-xs text-neutral-400 mt-1">{color.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-medium mb-4">{element.title}</h3>
                  <p className="text-neutral-400 font-light leading-relaxed">
                    {element.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Hero Video */}
          <motion.div
            className="bg-neutral-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <div className="h-96 bg-black overflow-hidden">
              <video
                src="/case-studies/smoove-move/hero-video.mov"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8">
              <h3 className="text-2xl font-medium mb-4">Hero Video</h3>
              <p className="text-neutral-400 font-light leading-relaxed">
                Custom-shot footage showcasing their moving services in action. The video creates immediate visual impact while maintaining professional quality.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Typography */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Typography</h2>
            <p className="text-neutral-400 font-light">Clean, professional, and unified across all touchpoints.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Typography Showcase */}
            <motion.div
              className="bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-800 p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="space-y-8">
                <div>
                  <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Roboto, sans-serif' }}>
                    Roboto
                  </div>
                  <div className="text-sm text-neutral-500 font-light">Primary Typeface</div>
                </div>

                <div className="space-y-4 pt-4 border-t border-neutral-800">
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Regular (400)</div>
                    <div className="text-2xl font-normal" style={{ fontFamily: 'Roboto, sans-serif' }}>
                      The quick brown fox jumps
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Medium (500)</div>
                    <div className="text-2xl font-medium" style={{ fontFamily: 'Roboto, sans-serif' }}>
                      The quick brown fox jumps
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Bold (700)</div>
                    <div className="text-2xl font-bold" style={{ fontFamily: 'Roboto, sans-serif' }}>
                      The quick brown fox jumps
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-neutral-800">
                  <div className="text-sm text-neutral-400 font-light leading-relaxed">
                    ABCDEFGHIJKLMNOPQRSTUVWXYZ<br/>
                    abcdefghijklmnopqrstuvwxyz<br/>
                    0123456789
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Typography Rationale */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div>
                <h3 className="text-2xl font-medium mb-4">Hierarchy through weight, not variety</h3>
                <p className="text-neutral-400 font-light leading-relaxed mb-4">
                  Instead of mixing multiple font families, we used Roboto exclusively and created visual hierarchy through weight variation. This approach keeps the design cohesive while providing clear distinction between headlines, body text, and UI elements.
                </p>
                <p className="text-neutral-400 font-light leading-relaxed mb-4">
                  From Light (300) for body text to Bold (700) for headlines, each weight serves a specific purpose. This creates natural visual levels without the jarring effect of switching between different typefaces.
                </p>
                <p className="text-neutral-400 font-light leading-relaxed">
                  The result is a cleaner, more professional appearance with better readability and a unified brand voice across all touchpoints.
                </p>
              </div>

              <div className="bg-neutral-950 rounded-xl p-6">
                <div className="text-sm font-medium mb-3">Why This Works</div>
                <ul className="space-y-2 text-sm text-neutral-400 font-light">
                  <li className="flex items-start gap-2">
                    <span className="text-white">—</span>
                    Visual consistency through single font family
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-white">—</span>
                    Clear hierarchy via weight variation (300-700)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-white">—</span>
                    Cleaner look than mixing multiple typefaces
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-white">—</span>
                    Superior readability across all devices
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Digital Experience */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Digital Experience</h2>
            <p className="text-neutral-400 font-light">Landing page built for conversions and performance.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-px bg-neutral-800">
            {digitalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-neutral-900 p-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-medium mb-3">{feature.title}</h3>
                <p className="text-neutral-400 font-light leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">The Results</h2>
            <p className="text-neutral-400 font-light">Measurable improvements across all metrics.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-neutral-800">
            {results.map((result, index) => (
              <motion.div
                key={result.metric}
                className="bg-neutral-900 p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true }}
              >
                <div className="text-sm text-neutral-500 mb-2 font-light">{result.metric}</div>
                <div className="text-2xl font-light mb-1 text-neutral-400 line-through">{result.before}</div>
                <div className="text-4xl font-light mb-3 text-white">{result.after}</div>
                <div className="text-xs text-neutral-400 font-light">{result.improvement}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-6 border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <svg className="w-12 h-12 mx-auto text-neutral-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>
            <blockquote className="text-2xl md:text-3xl font-light text-white mb-8 leading-relaxed">
              "I had a vision for what I wanted, but I couldn't execute it myself. <span className="font-black">chipahauk</span> took that concept and brought it to life perfectly. The design exceeded my expectations. They understood exactly what I was going for and delivered something even better than I imagined."
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="text-left">
                <div className="font-medium text-white">Arturo</div>
                <div className="text-sm text-neutral-500 font-light">Owner, Smoove Move Logistics</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 px-6 border-t border-neutral-800">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="flex items-center justify-center gap-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-neutral-600 font-light uppercase tracking-wider">Built with</span>
            <div className="flex items-center gap-4">
              {techStack.map((tech, index) => (
                <motion.span
                  key={tech}
                  className="text-sm text-neutral-500 font-light"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                >
                  {index > 0 && <span className="text-neutral-700 mx-4">·</span>}
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 border-t border-neutral-800">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Ready for a transformation like this?
            </h2>
            <p className="text-neutral-400 font-light mb-8">
              Let's create something extraordinary together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services#quote"
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors duration-200"
              >
                Start Your Project
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-3 border border-white text-white text-sm font-medium rounded-full hover:bg-white hover:text-black transition-all duration-300"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default SmooveMove
